// ---------------------------------------------------------------------------
// AudioControls — the gold play/pause button with a download button to its
// immediate right. ONE component for every place a song can be played: the
// admin Results panel and viewer page, Judges & Assignments, the legacy
// /admin/results page and the judge dashboard.
//
// History: each page kept its own copy of the play button, and only the
// Results panel had a download at all — a small bare arrow tucked UNDER the
// play button, which people did not recognise as a download (10 Sep 2026).
// The download is now a solid navy circle the same shape as the play button,
// a little larger, sitting beside it, with a tooltip. Inline styles rather
// than admin.css classes so it looks identical on the judge pages, which do
// not load that stylesheet.
//
// `playerKey` lets the same song appear twice on one page (the Results panel
// shows winners in Most celebrated AND in the table): each instance needs its
// own <audio> id or toggling one drives the other.
// ---------------------------------------------------------------------------

const PLAY_SIZE     = 26;
const DOWNLOAD_SIZE = 30;
const GOLD  = '#c9a84c';
const GOLD_HOVER = '#b8962e';
const NAVY  = '#1a1a2e';
const NAVY_HOVER = '#3b3b6b';

export function InlinePlayButton({ songId, audioUrl, playingId, onToggle, playerKey }) {
  const key = playerKey ?? songId;
  const isPlaying = playingId === key;
  return (
    <>
      <audio
        id={`audio-${key}`}
        src={audioUrl}
        preload="none"
        style={{ display: 'none' }}
        onEnded={() => onToggle(key, false)}
      />
      <button
        type="button"
        aria-label={isPlaying ? 'Pause' : 'Play'}
        title={isPlaying ? 'Pause' : 'Play'}
        onClick={(e) => { e.stopPropagation(); onToggle(key, !isPlaying); }}
        style={{
          width: `${PLAY_SIZE}px`, height: `${PLAY_SIZE}px`, borderRadius: '50%',
          background: GOLD, border: 'none', cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '11px', color: '#fff', flexShrink: 0,
          verticalAlign: 'middle', lineHeight: 1,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = GOLD_HOVER; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = GOLD; }}
      >
        {isPlaying ? '⏸' : '▶'}
      </button>
    </>
  );
}

/**
 * Play button with the download button immediately to its right.
 *
 * The download uses the audio path directly rather than the stored absolute
 * URL, so it works whichever host the reader is on. `download` asks the
 * browser to save rather than navigate; the filename falls back to the stored
 * one. Renders a fixed-width spacer when there is no audio so rows stay aligned.
 */
export default function AudioControls({ songId, audioUrl, playingId, onToggle, playerKey }) {
  const width = PLAY_SIZE + 6 + DOWNLOAD_SIZE;
  if (!audioUrl) {
    return <span style={{ display: 'inline-block', width: `${width}px`, height: `${DOWNLOAD_SIZE}px`, flexShrink: 0 }} />;
  }
  const fileName = String(audioUrl).split('/').pop() || 'song';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', width: `${width}px`, flexShrink: 0 }}>
      <InlinePlayButton
        songId={songId}
        playerKey={playerKey}
        audioUrl={audioUrl}
        playingId={playingId}
        onToggle={onToggle}
      />
      <a
        href={audioUrl}
        download={fileName}
        onClick={(e) => e.stopPropagation()}
        title={`Download ${fileName}`}
        aria-label="Download audio for this song"
        style={{
          width: `${DOWNLOAD_SIZE}px`, height: `${DOWNLOAD_SIZE}px`, borderRadius: '50%',
          background: NAVY, color: '#fff', textDecoration: 'none',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '16px', fontWeight: 700, lineHeight: 1, flexShrink: 0,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = NAVY_HOVER; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = NAVY; }}
      >
        ⤓
      </a>
    </span>
  );
}
